import {Component, OnInit, Input, Output, EventEmitter, ViewChild, ElementRef, SimpleChanges, Inject, AfterViewInit} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Model_TemplateDocument, Zone, Enum_TypeDonnee } from '../ged-designer.model';
import { FormGroup, FormArray, FormBuilder, Validators } from '@angular/forms';
import { fromEvent } from 'rxjs';

@Component({
    selector: 'abw-ged-designer-modal',
    templateUrl: './ged-designer-modal.component.html',
    styleUrls: ['./ged-designer-modal.component.scss']
})
export class GedDesignerModalComponent implements OnInit, AfterViewInit {
    @Input() template: Model_TemplateDocument = new Model_TemplateDocument();
    @Output() closeDialog: EventEmitter<boolean> = new EventEmitter<boolean>(false);
    @Output() configSaved: EventEmitter<Model_TemplateDocument> = new EventEmitter<Model_TemplateDocument>();

    @ViewChild('canvas', {static: false}) canvas: ElementRef;
    @ViewChild('file', {static: true}) inputFile: ElementRef;
    private _ctx: CanvasRenderingContext2D;
    private _canvasEl: HTMLCanvasElement = null;
    private _isMouseDown = false;

    preview: string = '';
    startX: number = 0;
    startY: number = 0;
    offsetX: number = 0;
    offsetY: number = 0;
    zone: Zone = new Zone();
    zones: Zone[] = [];
    zoneSelected: Zone = undefined;
    zoneMarker: Zone = undefined;
    setZoneMarker: boolean = false;
    templateForm: FormGroup;
    ZONES: FormArray;
    Enum_TypeDonnee = Enum_TypeDonnee;
    
    constructor(
        private _formBuilder: FormBuilder,
        public dialogRef: MatDialogRef<GedDesignerModalComponent>,
        @Inject(MAT_DIALOG_DATA) public data: { template?: any },
    ) {
    }

    ngOnInit() {
        this._doInitForms();
    }

    dismiss() {
        this.dialogRef.close();
    }

    private _doInitForms(): void {
        this.templateForm = this._formBuilder.group({
            ID: [''],
            NOM: ['', Validators.required],
            FILE_TEMPLATE: ['', Validators.required],
            ZONE_MARQUEUR: [null, Validators.required],
            TEXT_MARQUEUR: [[], Validators.required],
            ZONES: this._formBuilder.array([])
        });
        this.ZONES = this.templateForm.controls["ZONES"] as FormArray;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['template'] !== undefined) {
            const template = changes['template'].currentValue;
            this.doLoadTemplateInfos(template)
        }
    }

    ngAfterViewInit(): void {
        this._doInitCanvas();
        if (!!this.data.template) {
            this.doLoadTemplateInfos(this.data.template);
        }
    }

    private _doCreateZoneForm(zone: Zone = new Zone()) {
        const zoneForm = this._formBuilder.group({
            NOM: [zone.NOM, Validators.required],
            TYPE: [zone.TYPE, Validators.required],
            x: zone.x,
            y: zone.y,
            h: zone.h,
            w: zone.w
        });
        this.ZONES.push(zoneForm);
    }

    private _doRemoveZone(i: number): void {
        this.ZONES = this.templateForm.get('ZONES') as FormArray;
        this.ZONES.removeAt(i);
    }

    private _doInitCanvas(): void {
        if (this.canvas === undefined)
            return;

        this._canvasEl = this.canvas.nativeElement;
        this.canvas.nativeElement.width = 0;
        this.canvas.nativeElement.height = 0;

        this._doCalculateOffset();

        this._ctx = this.canvas.nativeElement.getContext('2d');
        this._ctx.lineWidth = 1;

        fromEvent(this._canvasEl, 'mousedown').subscribe(e => {
            this.handleMouseDown(e);
        });

        fromEvent(this._canvasEl, 'mouseup').subscribe(e => {
            this.handleMouseUp(e);
        })

        fromEvent(this._canvasEl, 'mousemove').subscribe(e => {
            this.handleMouseMove(e);
        })

        this._drawZones();
    }

    private _doCalculateOffset(): void {
        const offset = this._canvasEl.getBoundingClientRect();
        this.offsetX = offset.left;
        this.offsetY = offset.top;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------
    onChangeSelectedImage(event): void {
        if (event.target.files && event.target.files.length > 0) {
            const file = event.target.files[0];
            if (file.type.indexOf('image') < 0) {
                alert('Veuillez sélectionner un fichier de type image')
                return;
            }
            new Promise((resolve, reject) => {
                const reader = new FileReader()
                reader.onloadend = () => {
                    let base64Img = (reader.result as string);
                    let base64Parts = base64Img.split(',');
                    if (base64Parts.length > 1) {
                        base64Img = base64Parts[1];
                    }
                    this.templateForm.patchValue({ FILE_TEMPLATE: base64Img })
                };
                reader.onerror = reject
                reader.readAsDataURL(file)
            });
            this.doPreview(file);
        }
    }

    doSetMarkerZone(): void {
        this.setZoneMarker = true;
    }

    private doPreview(file: any): void {
        const reader = new FileReader();
        reader.onload = (_event: any) => {
            this.preview = _event.target.result;
            let img = new Image();
            img.src = _event.target.result;
            img.onload = () => {
                const fileSelected = document.querySelector('#file') as any;
                this.canvas.nativeElement.width = fileSelected.naturalWidth;
                this.canvas.nativeElement.height = fileSelected.naturalHeight;
                this._drawZones();
            }
        }
        reader.readAsDataURL(file);
    }

    doLoadTemplateInfos(template: Model_TemplateDocument): void {
        this.doReset();
        if(template !== null && template !== undefined) {
            this.templateForm.patchValue({
                ID: template.ID,
                NOM: template.NOM,
                ZONE_MARQUEUR: template.ZONE_MARQUEUR,
                TEXT_MARQUEUR: template.TEXT_MARQUEUR,
                FILE_TEMPLATE: template.FILE_TEMPLATE
            });
            this.ZONES = this.templateForm.controls["ZONES"] as FormArray;
            let base64Img: string = this.templateForm.get('FILE_TEMPLATE').value;
            const blob = this._dataURItoBlob(base64Img);
            /*this.templateForm.patchValue({
                FILE_TEMPLATE: blob
            })*/
            this.doPreview(blob);

            template.ZONES.map((zone, index) => {
                this._doCreateZoneForm(zone);
            });
            this.zones = template.ZONES;
            this.zoneMarker = template.ZONE_MARQUEUR;
        }
    }

    private _dataURItoBlob(dataURI): any {
        const byteString = window.atob(dataURI);
        const arrayBuffer = new ArrayBuffer(byteString.length);
        const int8Array = new Uint8Array(arrayBuffer);
        for (let i = 0; i < byteString.length; i++) {
            int8Array[i] = byteString.charCodeAt(i);
        }
        return new Blob([int8Array], {type: 'image/png'});
    }

    private handleMouseDown(e: any): void {
        this._doCalculateOffset();
        this.startX = e.clientX - this.offsetX;
        this.startY = e.clientY - this.offsetY;
        this._isMouseDown = true;
    }

    private handleMouseUp(e: any): void {
        this._isMouseDown = false;
        if (this.zone.x != 0 || this.zone.y != 0 || this.zone.w != 0 || this.zone.h != 0) {
            let zone = this._doProcessZone(this.zone);
            if (this.setZoneMarker) {
                this.zoneMarker = zone;
                this.setZoneMarker = false;
                this.templateForm.patchValue({
                    ZONE_MARQUEUR: this.zone
                });
            } else {
                this.zones.push(this._doProcessZone(zone));
                this._doCreateZoneForm(zone);
            }
        }
        this.zone = new Zone();
        this._drawZones();
    }

    private _doProcessZone(zone: Zone): Zone {
        const newZone = Object.assign({}, zone);
        if (newZone.w < 0) {
            newZone.x = newZone.x + newZone.w;
            newZone.w = Math.abs(newZone.w);
        }
        if (newZone.h < 0) {
            newZone.y = newZone.y + newZone.h;
            newZone.h = Math.abs(newZone.h);
        }
        return newZone;
    }

    private handleMouseMove(e: any): void {
        // if we're not dragging, just return
        if (!this._isMouseDown) {
            return;
        }

        // get the current mouse position
        const mouseX = e.clientX - this.offsetX;
        const mouseY = e.clientY - this.offsetY;

        // Put your mousemove stuff here

        // clear the canvas
        //this._ctx.clearRect(0, 0, this._canvasEl.width, this._canvasEl.height);
        this._drawZones();

        // calculate the rectangle width/height based
        // on starting vs current mouse position
        const width = mouseX - this.startX;
        const height = mouseY - this.startY;

        // draw a new rect from the start position
        // to the current mouse position
        this._ctx.strokeStyle = "black";
        this._ctx.strokeRect(this.startX, this.startY, width, height);
        this.zone.x = this.startX;
        this.zone.y = this.startY;
        this.zone.w = width;
        this.zone.h = height;
    }

    private _drawZones(): void {
        this._ctx.clearRect(0, 0, this._canvasEl.width, this._canvasEl.height);

        // draw zones
        this.zones.map((zone: Zone) => {
            if (this.zoneSelected !== zone) {
                this._ctx.strokeStyle = "green";
                this._ctx.strokeRect(zone.x, zone.y, zone.w, zone.h);
            }
        })

        if (this.zoneSelected !== undefined) {
            this._ctx.strokeStyle = "blue";
            this._ctx.strokeRect(this.zoneSelected.x, this.zoneSelected.y, this.zoneSelected.w, this.zoneSelected.h);
        }

        if (this.zoneMarker !== undefined) {
            this._ctx.strokeStyle = "red";
            this._ctx.strokeRect(this.zoneMarker.x, this.zoneMarker.y, this.zoneMarker.w, this.zoneMarker.h);
        }
    }

    onAddMarker(event: any): void {
        const value = event.value;
        const input = event.input;

        let markers = this.templateForm.get('TEXT_MARQUEUR').value;
        if (value && !markers.includes(value))
            markers.push(value);

        this.templateForm.patchValue({
            TEXT_MARQUEUR: markers
        });

        if (input)
            input.value = '';
    }

    onRemoveMarker(marker: string): void {
        const index = this.templateForm.get('TEXT_MARQUEUR').value.indexOf(marker);
        if (index >= 0)
            this.templateForm.get('TEXT_MARQUEUR').value.splice(index, 1);
    }

    doSaveTemplate(): void {
        if (this.templateForm.invalid)
            return;

        this.template = this.templateForm.getRawValue() as Model_TemplateDocument;
        this.configSaved.emit(this.template);
        this.dialogRef.close(this.template);
    }

    doReset(): void {
        this.setZoneMarker = false;
        this.templateForm.reset();
        this.templateForm.patchValue({
            ID: '',
            NOM: '',
            FILE_TEMPLATE: null,
            TEXT_MARQUEUR: [],
            ZONE_MARQUEUR: null,
        })
        this.preview = '';
        this.ZONES.clear();
        this.inputFile.nativeElement.value = '';
        //this.template.TEXT_MARQUEUR = [];
        //this.template.FILE_TEMPLATE = null;

        this.zones = [];
        this.zoneMarker = undefined;
        this.zoneSelected = undefined;
    }

    onSelectZone(zone: Zone): void {
        this.zoneSelected = zone;
        this._drawZones();
    }

    doDeleteSelectedZone(zone: Zone): void {
        this.zoneSelected = zone;
        const index = this.zones.indexOf(zone);
        if (index > -1) {
            this.zones.splice(index, 1);
            this.zoneSelected = undefined;
            this._drawZones();
            this._doRemoveZone(index);
        }
    }

    doCloseDialog(): void {
        this.closeDialog.emit(true);
    }

    /*private _doGetFormValidationErrors() {
        Object.keys(this.templateForm.controls).forEach(key => {
            const controlErrors: ValidationErrors = this.templateForm.get(key).errors;
            console.log('controlErrors', controlErrors)
            if (controlErrors != null) {
                Object.keys(controlErrors).forEach(keyError => {
                    console.log('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);
                });
            }
        });
    }*/
}